/* import __COLOCATED_TEMPLATE__ from './tolerances.hbs'; */
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { service } from '@ember/service';
import { tracked as makeTracked } from 'tracked-built-ins';
import type SiteService from '@ebiz/ember-aditools-site/services/aditools-site';
import type ToleranceInput from '../../models/inputs/tolerance';
import type ToleranceAdvisor from '../../models/advisors/tolerance';
import type Owner from '@ember/owner';

interface ToleranceSettingsArgs {
  model: ToleranceInput;
  advisor: ToleranceAdvisor;
  updateModel?: (inputType: string, modeldata: ToleranceInput) => void;
}

export default class TolerancesSettings extends Component<ToleranceSettingsArgs> {
  @service('aditools-site') declare siteconfig: SiteService;
  @tracked workingModel: ToleranceInput;

  ////////////////////////////////////////////////////////////////////////////////
  // Initialization                                                             //
  ////////////////////////////////////////////////////////////////////////////////

  constructor(owner: Owner, args: ToleranceSettingsArgs) {
    super(owner, args);
    this.workingModel = makeTracked(this.args.advisor.clone(this.args.model));
  }

  get capacitorToleranceSelection(): { text: string; value: string } | undefined {
    const selected = this.workingModel.capacitorTolerance;
    const selection = this.workingModel.capacitorToleranceList.find((item: { value: string }) => {
      return item.value === selected;
    });
    return selection;
  }

  get resistorToleranceSelection(): { text: string; value: string } | undefined {
    const selected = this.workingModel.resistorTolerance;
    const selection = this.workingModel.resistorToleranceList.find((item: { value: string }) => {
      return item.value === selected;
    });
    return selection;
  }

  get inductorToleranceSelection(): { text: string; value: string } | undefined {
    const selected = this.workingModel.inductorTolerance;
    const selection = this.workingModel.inductorToleranceList.find((item: { value: string }) => {
      return item.value === selected;
    });
    return selection;
  }

  get opAmpGbwToleranceSelection(): { text: string; value: string } | undefined {
    const selected = this.workingModel.opAmpGbwTolerance;
    const selection = this.workingModel.opAmpGbwToleranceList.find((item: { value: string }) => {
      return item.value === selected;
    });
    return selection;
  }

  get capacitorPreferredSeriesSelection(): { text: string; value: string } | undefined {
    const selected = this.workingModel.capacitorPreferredSeries;
    const selection = this.workingModel.capacitorPreferredSeriesList.find((item: { value: string }) => {
      return item.value === selected;
    });
    return selection;
  }

  get resistorPreferredSeriesSelection(): { text: string; value: string } | undefined {
    const selected = this.workingModel.resistorPreferredSeries;
    const selection = this.workingModel.resistorPreferredSeriesList.find((item: { value: string }) => {
      return item.value === selected;
    });
    return selection;
  }

  get inductorPreferredSeriesSelection(): { text: string; value: string } | undefined {
    const selected = this.workingModel.inductorPreferredSeries;
    const selection = this.workingModel.inductorPreferredSeriesList.find((item: { value: string }) => {
      return item.value === selected;
    });
    return selection;
  }

  get hasInductors(): boolean {
    return this.args.advisor.hasInductors();
  }

  ////////////////////////////////////////////////////////////////////////////////
  // Utility Functions                                                          //
  ////////////////////////////////////////////////////////////////////////////////

  updateCurrentModel(): void {
    if (this.args.updateModel && this.args.advisor) {
      this.args.updateModel('tolerances', this.args.advisor.clone(this.workingModel));
    }
  }

  updateComponentsModel(): void {
    if (this.args.advisor) {
      this.args.advisor.updateComponentsModel(this.workingModel);
    }
  }
  ////////////////////////////////////////////////////////////////////////////////
  // Component Actions                                                          //
  ////////////////////////////////////////////////////////////////////////////////

  @action setCapacitorTolerance(e: { text: string; value: string }): void {
    this.workingModel.capacitorTolerance = e.value;
    this.updateCurrentModel();
  }

  @action setResistorTolerance(e: { text: string; value: string }): void {
    this.workingModel.resistorTolerance = e.value;
    this.updateCurrentModel();
    this.updateComponentsModel();
  }

  @action setInductorTolerance(e: { text: string; value: string }): void {
    this.workingModel.inductorTolerance = e.value;
    this.updateCurrentModel();
  }

  @action setOpAmpGbwTolerance(e: { text: string; value: string }): void {
    this.workingModel.opAmpGbwTolerance = e.value;
    this.updateCurrentModel();
  }

  @action setCapacitorPreferredSeries(e: { text: string; value: string }): void {
    this.workingModel.capacitorPreferredSeries = e.value;
    this.updateCurrentModel();
  }

  @action setResistorPreferredSeries(e: { text: string; value: string }): void {
    this.workingModel.resistorPreferredSeries = e.value;
    this.updateCurrentModel();
  }

  @action setInductorPreferredSeries(e: { text: string; value: string }): void {
    this.workingModel.inductorPreferredSeries = e.value;
    this.updateCurrentModel();
  }
}
